import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, Lightbulb, Target, Zap, MessageCircle, Users, Star, ArrowRight, Sparkles, Coffee, Sunrise, Waves, Brain, Compass, Flame, Edit2, Volume2, VolumeX, LucideIcon } from 'lucide-react';

// Question stories and contexts
const questionContexts: Record<string, {
  story: string;
  icon: LucideIcon;
  gradient: string;
  conclusion: {
    title: string;
    message: string;
    reflection: string;
  };
}> = {
  motivation: {
    story: "Era uma manhã de segunda em Carneiros. O sol nascia dourado sobre o mar, e João, dono de uma pousada há 15 anos, tomava seu café na varanda. Ele lembrou do dia em que decidiu automatizar as primeiras respostas aos hóspedes. Não foi por tecnologia. Foi por algo mais profundo...",
    icon: Heart,
    gradient: "from-pink-400 to-red-500",
    conclusion: {
      title: "Sua motivação é única",
      message: "Como João descobriu, a verdadeira automação começa quando entendemos profundamente POR QUE queremos mudar.",
      reflection: "Guarde isso: sua motivação inicial será sua bússola nos momentos difíceis."
    }
  },
  guests: {
    story: "Ana gerencia reservas há 5 anos. Ela percebeu que cada hóspede tem um momento mágico diferente - para uns é o check-in sem fricção, para outros é aquela resposta às 23h sobre o café da manhã. Ela se perguntava: 'Se eu pudesse transformar apenas UM momento...'",
    icon: Users,
    gradient: "from-blue-400 to-purple-500",
    conclusion: {
      title: "Você escolheu seu momento",
      message: "Esse momento que você identificou? É onde a mágica acontece. É onde tecnologia vira hospitalidade.",
      reflection: "Lembre-se: grandes transformações começam com um momento específico."
    }
  },
  delight: {
    story: "Carlos nunca esqueceu o dia em que um hóspede disse: 'Vocês lembraram que eu sou vegetariano!' Era um detalhe pequeno, automático, mas o sorriso foi enorme. Ele entendeu que as melhores surpresas são as que ninguém pediu...",
    icon: Star,
    gradient: "from-yellow-400 to-orange-500",
    conclusion: {
      title: "Você tem o toque especial",
      message: "Essa pequena surpresa que você imaginou? É o que transforma uma estadia em uma memória.",
      reflection: "Os melhores sistemas não só respondem - eles encantam."
    }
  },
  constraints: {
    story: "Maria tinha 2 funcionários, orçamento apertado e 48 horas no dia que não eram suficientes. Um dia, ela parou de lutar contra as limitações e começou a abraçá-las como guias. Foi quando tudo mudou...",
    icon: Target,
    gradient: "from-green-400 to-teal-500",
    conclusion: {
      title: "Suas restrições são superpoderes",
      message: "Como Maria descobriu, limitações forçam criatividade. Elas nos mostram o que realmente importa.",
      reflection: "Suas restrições não são obstáculos - são o molde do seu sucesso único."
    }
  },
  clarity: {
    story: "Pedro passava noites acordado com mil ideias. Um dia, sua filha de 7 anos perguntou: 'Pai, se você pudesse consertar só uma coisa essa semana, qual seria?' A simplicidade da pergunta mudou tudo...",
    icon: Lightbulb,
    gradient: "from-purple-400 to-pink-500",
    conclusion: {
      title: "Você encontrou seu foco",
      message: "Esse problema único que você escolheu? É seu norte. Resolva isso bem, e o resto seguirá.",
      reflection: "Clareza é o primeiro passo para velocidade."
    }
  },
  north_star: {
    story: "Lucia media tudo - ocupação, RevPAR, TripAdvisor... Até o dia em que percebeu: estava medindo o barco, não o destino. Ela precisava de uma estrela-guia, uma métrica que dissesse 'estamos no caminho certo'...",
    icon: Compass,
    gradient: "from-indigo-400 to-blue-500",
    conclusion: {
      title: "Sua estrela-guia está definida",
      message: "Essa métrica que você escolheu será sua verdade. Simples, clara, impossível de ignorar.",
      reflection: "Uma boa métrica não só mede - ela inspira ação."
    }
  },
  ops: {
    story: "Às 15h de toda sexta, a equipe de Marcos suspirava. Era hora de compilar as disponibilidades para o fim de semana. 2 horas de trabalho repetitivo que todos odiavam. Marcos pensou: 'E se isso sumisse?'",
    icon: Zap,
    gradient: "from-amber-400 to-red-500",
    conclusion: {
      title: "Você vai libertar sua equipe",
      message: "Essa tarefa chata que você identificou? Automatizá-la não é sobre tecnologia - é sobre dignidade no trabalho.",
      reflection: "As melhores automações devolvem humanidade ao trabalho."
    }
  },
  tone: {
    story: "Rita atendia hóspedes há anos. Ela sabia exatamente quando ser formal ('Certamente, senhora') e quando ser calorosa ('Oi! Claro que temos!'). O desafio era ensinar isso a um bot...",
    icon: MessageCircle,
    gradient: "from-teal-400 to-green-500",
    conclusion: {
      title: "Sua voz está clara",
      message: "Esse tom que você definiu? É a alma da sua pousada em cada mensagem.",
      reflection: "Bots podem ter personalidade quando sabemos qual é a nossa."
    }
  },
  love_the_problem: {
    story: "No final do dia, depois de responder todas essas perguntas, Gustavo percebeu algo. Não era sobre a solução perfeita. Era sobre se apaixonar pelo problema certo. E quando leu tudo de novo, um problema brilhou mais forte...",
    icon: Flame,
    gradient: "from-red-500 to-orange-600",
    conclusion: {
      title: "Você encontrou SEU problema",
      message: "Esse problema que te deixa animado? É onde sua energia vai render mais. É onde a mágica vai acontecer.",
      reflection: "Apaixone-se pelo problema, não pela solução. As soluções mudam, os problemas importantes permanecem."
    }
  }
};

// Get question by ID or tag
const getQuestionData = (identifier: string) => {
  // Import questions from ProblemExplorer
  const questions = [
    {
      id: 1,
      set: "I",
      tag: "motivation",
      question: "O que te atraiu primeiro na ideia de automatizar respostas para uma pousada de praia — e por quê?"
    },
    {
      id: 2,
      set: "I",
      tag: "guests",
      question: "Qual momento da jornada do hóspede (descoberta, reserva, check-in, estadia, check-out) você mais gostaria de transformar com AI?"
    },
    {
      id: 3,
      set: "I",
      tag: "delight",
      question: "Que pequena surpresa (um uau sutil) você adoraria que o sistema entregasse sem ninguém pedir?"
    },
    {
      id: 4,
      set: "I",
      tag: "constraints",
      question: "Quais são as três maiores restrições (tempo, orçamento, equipe) e como você pode abraçá-las como guias de design?"
    },
    {
      id: 5,
      set: "I",
      tag: "clarity",
      question: "Se você pudesse resolver só um problema esta semana, qual seria e como saberá que ficou bom?"
    },
    {
      id: 6,
      set: "I",
      tag: "north_star",
      question: "Qual métrica simples poderia ser sua North Star (ex.: tempo médio de resposta, NPS de mensagens, conversão de reservas)?"
    },
    {
      id: 7,
      set: "I",
      tag: "ops",
      question: "Qual tarefa repetitiva a equipe mais odeia e como o bot poderia reduzi-la em 80%?"
    },
    {
      id: 8,
      set: "I",
      tag: "tone",
      question: "Se o bot fosse uma pessoa da equipe, seria mais como quem: o concierge discreto ou o recepcionista animado?"
    },
    {
      id: 36,
      set: "III",
      tag: "love_the_problem",
      question: "Quando você lê estas perguntas, qual problema específico te deixa mais animado hoje — e qual próximo passo micro valida isso?"
    }
  ];

  // Find by ID or tag
  const question = questions.find(q => 
    q.id === parseInt(identifier) || q.tag === identifier
  );

  return question;
};

export default function SingleQuestionExperience() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();
  const [stage, setStage] = useState<'intro' | 'question' | 'conclusion'>('intro');
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAnswer, setEditedAnswer] = useState('');
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const question = getQuestionData(identifier || '');
  const context = question ? questionContexts[question.tag] : null;

  const API_URL = import.meta.env.VITE_API_URL || 'https://biig-api.b-9f2.workers.dev';

  // Initialize session
  useEffect(() => {
    const initSession = async () => {
      try {
        const response = await fetch(`${API_URL}/api/problem-explorer/session`);
        if (response.ok) {
          const data = await response.json();
          setSessionId(data.session.session_id);
        }
      } catch (error) {
        console.error('Error creating session:', error);
      }
    };
    initSession();
  }, []);

  if (!question || !context) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Pergunta não encontrada</h1>
          <button 
            onClick={() => navigate('/problems')}
            className="text-blue-500 hover:text-blue-600"
          >
            Ver todas as perguntas
          </button>
        </div>
      </div>
    );
  }

  const IconComponent = context.icon;

  const handleAnswer = async () => {
    if (!answer.trim()) return;

    setSaving(true);
    try {
      // Save to database
      if (sessionId) {
        await fetch(`${API_URL}/api/problem-explorer/answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            questionId: question.id,
            questionText: question.question,
            questionSet: question.set,
            questionTag: question.tag,
            answer
          })
        });
      }
      setStage('conclusion');
      // Play music when entering conclusion
      playMusic();
    } catch (error) {
      console.error('Error saving answer:', error);
      setStage('conclusion');
      playMusic();
    } finally {
      setSaving(false);
    }
  };

  const playMusic = () => {
    try {
      const audio = new Audio('/audio/Infinitivo.mp3');
      audio.loop = true; // Loop for that infinite feeling
      audio.volume = 0.3; // Gentle background volume
      audio.play().catch(e => console.log('Audio playback failed:', e));
      setAudioRef(audio);
    } catch (error) {
      console.log('Music not available yet');
    }
  };

  // Cleanup audio when component unmounts or stage changes
  useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
        audioRef.currentTime = 0;
      }
    };
  }, [audioRef]);

  const handleEditAnswer = () => {
    setEditedAnswer(answer);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editedAnswer.trim()) return;

    setSaving(true);
    try {
      // Update in database
      if (sessionId) {
        await fetch(`${API_URL}/api/problem-explorer/answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            questionId: question.id,
            questionText: question.question,
            questionSet: question.set,
            questionTag: question.tag,
            answer: editedAnswer
          })
        });
      }
      setAnswer(editedAnswer);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating answer:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedAnswer('');
  };

  const toggleMute = () => {
    if (audioRef) {
      if (isMuted) {
        audioRef.volume = 0.3;
      } else {
        audioRef.volume = 0;
      }
      setIsMuted(!isMuted);
    }
  };

  if (stage === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${context.gradient} mb-6`}>
              <IconComponent className="text-white" size={32} />
            </div>
            
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
              Uma pergunta para você
            </h1>
            
            <div className="prose prose-lg text-gray-600 mb-8">
              <p className="leading-relaxed">
                {context.story}
              </p>
            </div>
            
            <button
              onClick={() => setStage('question')}
              className={`w-full bg-gradient-to-r ${context.gradient} text-white py-4 px-6 rounded-2xl font-medium text-lg hover:shadow-lg transition-all flex items-center justify-center gap-3`}
            >
              Continuar
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'question') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${context.gradient} mb-6`}>
              <IconComponent className="text-white" size={32} />
            </div>
            
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
              {question.question}
            </h2>
            
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Responda com o coração. Não há resposta certa ou errada..."
              className="w-full h-32 md:h-40 p-4 border-2 border-gray-200 rounded-2xl text-gray-700 focus:border-blue-400 focus:outline-none resize-none text-lg"
              autoFocus
            />
            
            <div className="mt-6 flex gap-4">
              <button
                onClick={() => setStage('intro')}
                className="flex-1 bg-gray-100 text-gray-600 py-4 px-6 rounded-2xl font-medium hover:bg-gray-200 transition-all"
              >
                Voltar
              </button>
              <button
                onClick={handleAnswer}
                disabled={!answer.trim() || saving}
                className={`flex-1 bg-gradient-to-r ${context.gradient} text-white py-4 px-6 rounded-2xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50`}
              >
                {saving ? 'Salvando...' : 'Enviar resposta'}
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stage === 'conclusion') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center relative">
            {/* Audio control in corner */}
            {audioRef && (
              <button
                onClick={toggleMute}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                title={isMuted ? "Ativar som" : "Silenciar"}
              >
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            )}
            
            <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${context.gradient} mb-6`}>
              <Sparkles className="text-white" size={32} />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              {context.conclusion.title}
            </h2>
            
            <p className="text-lg text-gray-600 mb-6">
              {context.conclusion.message}
            </p>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 mb-8">
              <p className="text-gray-700 italic">
                "{context.conclusion.reflection}"
              </p>
            </div>
            
            {answer && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 text-left">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm font-medium text-gray-600">Sua resposta:</p>
                  {!isEditing && (
                    <button
                      onClick={handleEditAnswer}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Editar
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <>
                    <textarea
                      value={editedAnswer}
                      onChange={(e) => setEditedAnswer(e.target.value)}
                      className="w-full h-32 p-3 border-2 border-purple-200 rounded-xl text-gray-700 focus:border-purple-400 focus:outline-none resize-none"
                      autoFocus
                    />
                    <div className="mt-3 flex gap-2 justify-end">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSaveEdit}
                        disabled={!editedAnswer.trim() || saving}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
                      >
                        {saving ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-800">{answer}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}